import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, CornerDownLeft, Command, ShoppingCart, Users, Skull, UserSquare2, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';

// searchDelay is how long the overlay waits after the last keystroke before
// asking the server. Long enough that a typed word costs one request, short
// enough that results appear to arrive immediately.
const searchDelay = 180;

const SearchOverlay = ({ onClose, onSearch, results, onSelect, loading }) => {
  const [query, setQuery] = useState('');
  // Index of the highlighted result for keyboard navigation. -1 means
  // "nothing highlighted yet", so Enter while typing still just no-ops.
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => onSearch(query), searchDelay);
    return () => clearTimeout(timer);
  }, [query, onSearch]);

  // A new query invalidates the previous highlight — otherwise Enter would
  // open a row from the old result set.
  useEffect(() => setActiveIndex(-1), [query, results]);

  const scrollActiveIntoView = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    const item = list.querySelectorAll('.search-result-item')[activeIndex];
    if (item) item.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  useEffect(() => {
    scrollActiveIntoView();
  }, [scrollActiveIntoView]);

  // Handle shortcuts: Esc closes, arrows move, Enter opens the highlight.
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      const count = results.length;
      if (count === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % count);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + count) % count);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const target = activeIndex >= 0 ? results[activeIndex] : results[0];
        if (target) onSelect(target);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, onSelect, results, activeIndex]);

  const getIcon = (type) => {
    switch (type) {
      case 'sale': return <ShoppingCart size={18} />;
      case 'harvest': return <Users size={18} />;
      case 'mortality': return <Skull size={18} />;
      case 'employee': return <UserSquare2 size={18} />;
      default: return <Search size={18} />;
    }
  };

  const showResults = results.length > 0;
  const showSpinner = loading && query.length >= 2;

  return (
    <div className="search-overlay-backdrop" onClick={onClose}>
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: -20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: -20 }}
        className="search-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="search-input-container">
          <Search className="search-icon" />
          <input 
            autoFocus
            type="text" 
            placeholder="Search for sales, agents, mortality or employees..." 
            className="search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            role="combobox"
            aria-expanded={showResults}
            aria-controls="search-results"
            aria-autocomplete="list"
            aria-label="Global search"
          />
          {showSpinner && <Loader2 className="search-spinner" size={18} />}
          <div className="search-shortcuts-hint">
            <span className="key-hint">ESC</span>
          </div>
        </div>

        <div id="search-results" ref={listRef} className="search-results-container custom-scrollbar" role="listbox">
          {showResults ? (
            <div className="results-list">
              <p className="results-header">
                {`Search Results · ${results.length}`}
              </p>
              {results.map((result, index) => (
                <button
                  type="button"
                  key={`${result.type}-${result.id}`}
                  onClick={() => onSelect(result)}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={`search-result-item group${index === activeIndex ? ' is-active' : ''}`}
                  role="option"
                  aria-selected={index === activeIndex}
                >
                  <div className="result-icon-box">
                    {getIcon(result.type)}
                  </div>
                  <div className="result-content">
                    <p className="result-title">
                      {result.title}
                    </p>
                    <p className="result-snippet">
                      {result.snippet}
                    </p>
                  </div>
                  <div className="result-action">
                    <CornerDownLeft className="action-icon" />
                  </div>
                </button>
              ))}
            </div>
          ) : showSpinner ? (
            <div className="search-empty-state">
              <div className="empty-icon-box">
                <Loader2 size={32} className="search-spinner-rotate" />
              </div>
              <p className="empty-hint">Searching…</p>
            </div>
          ) : query.length >= 2 ? (
            <div className="search-empty-state">
              <div className="empty-icon-box">
                <Search size={32} />
              </div>
              <p className="empty-text">No records found matching "<span className="highlight-text">{query}</span>"</p>
              <p className="empty-hint">Try a buyer name, tally sheet number, employee, or prefix the query with "sale", "harvest", "mortality" or "employee".</p>
            </div>
          ) : (
            <div className="search-empty-state">
              <p className="empty-hint">Start typing to search across Farm Admin...</p>
              <p className="empty-hint search-scope-hint">
                Scope a search by starting with <code>sale</code>, <code>harvest</code>, <code>mortality</code> or <code>employee</code>.
              </p>
            </div>
          )}
        </div>

        <div className="search-footer">
          <div className="footer-command">
            <Command size={12} />
            <span>K for global search</span>
          </div>
          <div className="footer-action">
            <CornerDownLeft size={12} />
            <span>Open Result</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SearchOverlay;
